"use client";

import { rmcClient } from "@/lib/rmcClient";
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useReactToPrint } from "react-to-print";

function pick(...values) {
  for (const v of values) {
    if (v !== undefined && v !== null && String(v).trim() !== "") return String(v);
  }
  return "";
}

function fmtQty(v, digits = 3) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  return n.toFixed(digits);
}

function fmtMoney(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function hasValue(v) {
  return v !== undefined && v !== null && String(v).trim() !== "";
}

/** Prefer invoice-level lines; some APIs only nest grade lines under each challan. */
function collectSalesInvoiceLines(source) {
  if (!source) return [];
  const direct = [source.items, source.invoice_items, source.lines].find(
    (a) => Array.isArray(a) && a.length > 0
  );
  if (direct) return direct;
  const chs = Array.isArray(source.challans) ? source.challans : [];
  return chs.flatMap((ch) =>
    (Array.isArray(ch?.items) ? ch.items : []).map((line) => ({
      ...line,
      challan_id: line.challan_id != null ? line.challan_id : ch?.id,
      challan_no: pick(line?.challan_no, ch?.challan_no),
    }))
  );
}

const InvoicePrint = forwardRef(function InvoicePrint({ invoice }, ref) {
  const contentRef = useRef(null);
  const [company, setCompany] = useState(null);
  const [freshInvoice, setFreshInvoice] = useState(null);

  useEffect(() => {
    (async () => {
      const c = (await rmcClient.getCompany()).data;
      setCompany(c);
    })();
  }, []);

  const printDocumentTitle = React.useMemo(() => {
    const tt = String(invoice?.tax_type ?? "").toUpperCase();
    const base = invoice?.invoice_no || "sales-invoice";
    if (tt === "NON_GST" || tt === "NON-GST") return `cash-memo-${base}`;
    return base;
  }, [invoice?.tax_type, invoice?.invoice_no]);

  const printFn = useReactToPrint({
    contentRef,
    documentTitle: printDocumentTitle,
  });

  const prepareFreshData = async () => {
    if (!invoice?.id) return;
    const full = (await rmcClient.getSalesInvoice(invoice.id)).data;
    flushSync(() => {
      setFreshInvoice(full);
    });
  };

  useImperativeHandle(ref, () => ({
    openPrint: async () => {
      if (!invoice) return;
      await prepareFreshData();
      await new Promise((resolve) => setTimeout(resolve, 0));
      printFn();
    },
  }));

  useEffect(() => {
    setFreshInvoice(null);
  }, [invoice?.id]);

  if (!invoice) {
    return <div ref={contentRef} className="hidden" aria-hidden />;
  }

  const source = freshInvoice || invoice;
  const taxTypeNorm = String(source?.tax_type ?? "").toUpperCase();
  const isGstInvoice = taxTypeNorm === "GST";
  /** NON_GST sales invoice prints as Cash Memo — simplified header (blessing still shown when set). */
  const isCashMemo = taxTypeNorm === "NON_GST" || taxTypeNorm === "NON-GST";
  const challans = Array.isArray(source?.challans) ? source.challans : [];
  const isMultiChallan = challans.length > 1;
  const challanById = new Map(
    challans
      .map((ch) => [Number(ch?.id), ch])
      .filter(([id]) => Number.isFinite(id) && id > 0)
  );
  const rawLines = collectSalesInvoiceLines(source);
  const items = rawLines.map((line) => {
    const lineChallanId = Number(line?.challan_id);
    const relatedChallan = Number.isFinite(lineChallanId) ? challanById.get(lineChallanId) : null;
    const qty = Number(line.qty_cum ?? line.qty);
    const rate = Number(line.rate);
    let amount = Number(line.amount);
    if (!Number.isFinite(amount) && Number.isFinite(qty) && Number.isFinite(rate)) {
      amount = qty * rate;
    }
    const challanNo = pick(line?.challan_no, relatedChallan?.challan_no);
    const vehicleNo = pick(
      line?.vehicle_number,
      line?.vehicle_no,
      relatedChallan?.vehicle_number,
      relatedChallan?.vehicle_no
    );
    const batchNo = pick(line?.batch_no, relatedChallan?.batch_no);
    const labelBase = line?.grade?.name || line?.grade_name || "—";
    const labelWithRefs = isMultiChallan
      ? [
          labelBase,
          challanNo ? `Challan: ${challanNo}` : "",
          vehicleNo ? `Vehicle: ${vehicleNo}` : "",
          batchNo ? `Batch: ${batchNo}` : "",
        ]
          .filter(Boolean)
          .join(" | ")
      : labelBase;
    return {
      ...line,
      label: labelWithRefs,
      qty: Number.isFinite(qty) ? qty : 0,
      rate: Number.isFinite(rate) ? rate : 0,
      amount: Number.isFinite(amount) ? amount : 0,
      remark: line?.remark || "",
    };
  });
  const totalQtyCum = items.reduce((sum, it) => sum + (Number.isFinite(it.qty) ? it.qty : 0), 0);
  const subTotal = Number(source.sub_total);
  const gstRate = Number(source.gst_rate || 0);
  const gstAmount = Number(source.gst_amount);
  const totalAmount = Number(source.total_amount);

  const companyName = pick(company?.name, company?.company_name) || "Company Name";
  const companyAddress = pick(company?.address, company?.address_line1);
  const companyPhone = pick(
    company?.contact_phone,
    company?.mobile_no,
    company?.mobile,
    company?.mobile_number,
    company?.phone,
    company?.contact_no
  );
  const companyGst = pick(company?.gst_number, company?.gstin);
  const companyEmail = pick(company?.email, company?.contact_email);
  const companyBlessing = pick(company?.blessing, company?.invoice_blessing);

  const partyObj = source?.party || {};
  const siteObj = source?.site || {};
  /** APIs often return linked challans only as `challans[]`, not nested `challan`. */
  const challanObj =
    challans.length === 1
      ? { ...challans[0], ...(source?.challan || {}) }
      : source?.challan || {};
  const partyName = pick(partyObj?.name);
  const partyAddress = pick(partyObj?.address, partyObj?.billing_address, partyObj?.address_line1);
  const partyPhone = pick(
    partyObj?.contact_no,
    partyObj?.phone,
    partyObj?.mobile_no,
    partyObj?.mobile,
    partyObj?.mobile_number
  );
  const partyGst = pick(partyObj?.gst_number, partyObj?.gstin);
  const siteName = pick(siteObj?.name, source?.site_name);
  const challanNo = pick(challanObj?.challan_no, source?.challan_no);
  const invoiceNo = pick(source?.invoice_no, source?.id ? `INV-${source.id}` : "");
  const invoiceDate = pick(source?.date, source?.invoice_date);
  const vehicleNo = pick(
    challanObj?.vehicle_number,
    challanObj?.vehicle_no,
    source?.vehicle_number,
    source?.vehicle_no
  );
  const batchNo = pick(challanObj?.batch_no);

  const leftInfoRows = [
    { label: "Invoice Number", value: invoiceNo },
    { label: "Invoice Date", value: invoiceDate },
    { label: "Challan No", value: isMultiChallan ? "" : challanNo },
    { label: "Site", value: siteName },
  ].filter((row) => hasValue(row.value));

  const rightInfoRows = [
    { label: "Vehicle Number", value: isMultiChallan ? "" : vehicleNo },
    { label: "Batch No.", value: isMultiChallan ? "" : batchNo },
  ].filter((row) => hasValue(row.value));
  const hasRemarkColumn = items.some((it) => hasValue(it.remark));
  const hasTaxColumn = isGstInvoice;
  const invoiceBankDetails = source?.bank_details || {};
  const bankName = pick(invoiceBankDetails?.bank_name, company?.bank_name);
  const bankAccountNo = pick(invoiceBankDetails?.bank_account_no, company?.bank_account_no, company?.account_number);
  const bankIfscCode = pick(invoiceBankDetails?.bank_ifsc_code, company?.bank_ifsc_code, company?.ifsc_code);
  const bankBranch = pick(invoiceBankDetails?.bank_branch, company?.bank_branch);
  const accountType = pick(invoiceBankDetails?.account_type, company?.account_type);
  const showBankDetails =
    hasTaxColumn &&
    (hasValue(bankName) ||
      hasValue(bankAccountNo) ||
      hasValue(bankIfscCode) ||
      hasValue(bankBranch) ||
      hasValue(accountType));
  const totalColumnCount = 5 + (hasTaxColumn ? 1 : 0) + (hasRemarkColumn ? 1 : 0);
  const totalsLeftColSpan = Math.max(1, totalColumnCount - 2);
  const emptyFillHeightPx = Math.max(110, 645 - items.length * 34);

  return (
    <div
      aria-hidden
      className="pointer-events-none fixed left-0 top-0 -z-10 h-0 w-0 overflow-hidden opacity-0 print:static print:z-50 print:h-auto print:w-auto print:overflow-visible print:opacity-100"
    >
      <div ref={contentRef} className="bg-white text-black print:block">
        <section className="mx-auto flex h-[297mm] w-[210mm] flex-col border border-slate-700 p-2 text-[10px] leading-tight">
          {hasValue(companyBlessing) && (
            <div className="mb-2 text-center text-[10px] italic text-slate-700 print:break-inside-avoid">
              {companyBlessing}
            </div>
          )}
          <div className="border border-slate-600">
            <div className="border-b border-slate-600 px-2 py-1.5 text-center">
              {isCashMemo ? (
                <p className="py-2 text-xl font-bold tracking-wide">Cash Memo</p>
              ) : (
                <>
                  <p className="text-[9px] font-semibold uppercase tracking-wide">Sales Invoice</p>
                  <p className="text-base font-bold leading-tight">{companyName}</p>
                  {hasValue(companyAddress) && <p className="text-[10px] text-slate-700">{companyAddress}</p>}
                  {(hasValue(companyPhone) || hasValue(companyEmail) || hasValue(companyGst)) && (
                    <p className="text-[10px] text-slate-700">
                      {hasValue(companyPhone) && `Mobile: ${companyPhone}`}
                      {hasValue(companyPhone) && hasValue(companyEmail) && " | "}
                      {hasValue(companyEmail) && `Email: ${companyEmail}`}
                      {(hasValue(companyPhone) || hasValue(companyEmail)) && hasValue(companyGst) && " | "}
                      {hasValue(companyGst) && `GSTIN: ${companyGst}`}
                    </p>
                  )}
                </>
              )}
            </div>

            {(leftInfoRows.length > 0 || rightInfoRows.length > 0) && (
              <div className="grid grid-cols-2 border-b border-slate-600">
                <div className="border-r border-slate-600 px-2 py-2">
                  {leftInfoRows.map((row) => (
                    <div key={row.label} className="mb-1 flex gap-2 last:mb-0">
                      <span className="min-w-[95px] font-semibold">{row.label}</span>
                      <span>: {row.value}</span>
                    </div>
                  ))}
                </div>
                <div className="px-2 py-2">
                  {rightInfoRows.map((row) => (
                    <div key={row.label} className="mb-1 flex gap-2 last:mb-0">
                      <span className="min-w-[95px] font-semibold">{row.label}</span>
                      <span>: {row.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(hasValue(partyName) || hasValue(partyAddress) || hasValue(partyPhone) || hasValue(partyGst)) && (
              <div className="grid grid-cols-2 border-b border-slate-600">
                <div className="border-r border-slate-600 px-2 py-2">
                  <p className="mb-1 font-semibold">Billing Details</p>
                  {hasValue(partyName) && (
                    <p>
                      <span className="font-semibold">Name</span>: {partyName}
                    </p>
                  )}
                  {(hasValue(partyPhone) || hasValue(partyGst)) && (
                    <p>
                      {hasValue(partyGst) && (
                        <>
                          <span className="font-semibold">GSTIN</span>: {partyGst}
                        </>
                      )}
                      {hasValue(partyGst) && hasValue(partyPhone) && " | "}
                      {hasValue(partyPhone) && (
                        <>
                          <span className="font-semibold">Mobile</span>: {partyPhone}
                        </>
                      )}
                    </p>
                  )}
                  {hasValue(partyAddress) && <p>{partyAddress}</p>}
                </div>
                <div className="px-2 py-2">
                  <p className="mb-1 font-semibold">Shipping Details</p>
                  {hasValue(siteName) && (
                    <p>
                      <span className="font-semibold">Site</span>: {siteName}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="mt-2">
            <table className="w-full border-collapse border border-slate-600">
              <thead>
                <tr className="bg-slate-100">
                  <th className="w-10 border border-slate-600 px-2 py-1 text-center">Sr.</th>
                  <th className="border border-slate-600 px-2 py-1 text-left">Item Description</th>
                  <th className="border border-slate-600 px-2 py-1 text-right">Quantity (CUM)</th>
                  <th className="border border-slate-600 px-2 py-1 text-right">Rate</th>
                  {hasTaxColumn && <th className="w-16 border border-slate-600 px-2 py-1 text-right">Tax %</th>}
                  {hasRemarkColumn && <th className="border border-slate-600 px-2 py-1 text-left">Remark</th>}
                  <th className="border border-slate-600 px-2 py-1 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {items.map((line, idx) => (
                  <tr key={idx}>
                    <td className="border border-slate-600 px-2 py-0.5 text-center align-top">{idx + 1}</td>
                    <td className="border border-slate-600 px-2 py-0.5 align-top">{line.label}</td>
                    <td className="border border-slate-600 px-2 py-0.5 text-right align-top">{fmtQty(line.qty) || "0.000"}</td>
                    <td className="border border-slate-600 px-2 py-0.5 text-right align-top">{fmtMoney(line.rate) || "0.00"}</td>
                    {hasTaxColumn && (
                      <td className="border border-slate-600 px-2 py-0.5 text-right align-top">{gstRate.toFixed(2)}</td>
                    )}
                    {hasRemarkColumn && <td className="border border-slate-600 px-2 py-0.5 align-top">{line.remark || ""}</td>}
                    <td className="border border-slate-600 px-2 py-0.5 text-right align-top">{fmtMoney(line.amount) || "0.00"}</td>
                  </tr>
                ))}
                <tr>
                  <td className="border border-slate-600 px-2 py-1" style={{ height: `${emptyFillHeightPx}px` }} />
                  <td className="border border-slate-600 px-2 py-1" />
                  <td className="border border-slate-600 px-2 py-1" />
                  <td className="border border-slate-600 px-2 py-1" />
                  {hasTaxColumn && <td className="border border-slate-600 px-2 py-1" />}
                  {hasRemarkColumn && <td className="border border-slate-600 px-2 py-1" />}
                  <td className="border border-slate-600 px-2 py-1" />
                </tr>
                <tr className="bg-slate-50">
                  <td className="border border-slate-600 px-2 py-1 text-right font-semibold" colSpan={2}>
                    Total
                  </td>
                  <td className="border border-slate-600 px-2 py-1 text-right font-semibold">
                    {fmtQty(totalQtyCum) || "0.000"}
                  </td>
                  <td className="border border-slate-600 px-2 py-1" />
                  {hasTaxColumn && <td className="border border-slate-600 px-2 py-1" />}
                  {hasRemarkColumn && <td className="border border-slate-600 px-2 py-1" />}
                  <td className="border border-slate-600 px-2 py-1" />
                </tr>
                <tr>
                  <td className="border border-slate-600 px-2 py-1" colSpan={totalsLeftColSpan} />
                  <td className="border border-slate-600 px-2 py-1 text-right font-semibold">Subtotal</td>
                  <td className="border border-slate-600 px-2 py-1 text-right">
                    {fmtMoney(Number.isFinite(subTotal) ? subTotal : 0)}
                  </td>
                </tr>
                {hasTaxColumn && (
                  <tr>
                    <td className="border border-slate-600 px-2 py-1" colSpan={totalsLeftColSpan} />
                    <td className="border border-slate-600 px-2 py-1 text-right font-semibold">
                      GST ({gstRate.toFixed(2)}%)
                    </td>
                    <td className="border border-slate-600 px-2 py-1 text-right">
                      {fmtMoney(Number.isFinite(gstAmount) ? gstAmount : 0)}
                    </td>
                  </tr>
                )}
                <tr>
                  <td className="border border-slate-600 px-2 py-1" colSpan={totalsLeftColSpan} />
                  <td className="border border-slate-600 px-2 py-1 text-right font-semibold">Total</td>
                  <td className="border border-slate-600 px-2 py-1 text-right font-semibold">
                    {fmtMoney(Number.isFinite(totalAmount) ? totalAmount : 0)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-auto grid grid-cols-2 gap-2 pt-1">
            <div className="min-h-24 border border-slate-600 p-2 text-[10px]">
              {showBankDetails && hasValue(bankName) && (
                <p>
                  <span className="font-semibold">Bank</span>: {bankName}
                </p>
              )}
              {showBankDetails && hasValue(bankAccountNo) && (
                <p>
                  <span className="font-semibold">A/C No</span>: {bankAccountNo}
                </p>
              )}
              {showBankDetails && hasValue(bankIfscCode) && (
                <p>
                  <span className="font-semibold">IFSC</span>: {bankIfscCode}
                </p>
              )}
              {showBankDetails && hasValue(bankBranch) && (
                <p>
                  <span className="font-semibold">Branch</span>: {bankBranch}
                </p>
              )}
              {showBankDetails && hasValue(accountType) && (
                <p>
                  <span className="font-semibold">Account Type</span>: {accountType}
                </p>
              )}
            </div>
            <div className="flex min-h-24 items-end justify-end border border-slate-600 p-2 text-[10px]">
              <p className="font-semibold">
                {isCashMemo ? "Authorized signatory" : `For ${companyName}`}
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
});

InvoicePrint.displayName = "InvoicePrint";

export default InvoicePrint;
